import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ShoppingCart, Search, ShieldCheck, Package, User, X, Phone } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import CartDrawer from "@/components/CartDrawer";
import { MegaMenuBar, MegaMenuMobile } from "@/components/MegaMenu";
import { useResponsive } from "@/context/ResponsiveContext";
import { useHeaderFooter } from "@/lib/headerFooter";
import acpLogo from "@/assets/acp-logo.jpg";

function SearchField({ autoFocus = false, onDone }) {
  const navigate = useNavigate();
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { if (autoFocus && inputRef.current) inputRef.current.focus(); }, [autoFocus]);

  useEffect(() => {
    if (!term || term.trim().length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/products", { params: { q: term.trim(), limit: 8 } })
        .then((r) => { setResults(r.data); setOpen(true); })
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [term]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const goSearch = () => {
    if (!term.trim()) return;
    setOpen(false); onDone && onDone();
    navigate(`/search/${encodeURIComponent(term.trim())}`);
  };
  const pick = (p) => { setOpen(false); setTerm(""); onDone && onDone(); navigate(`/product/${p.slug || p.id}`); };

  return (
    <div ref={boxRef} className="relative w-full">
      <form onSubmit={(e) => { e.preventDefault(); goSearch(); }}>
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground z-10" size={18} />
        <Input
          ref={inputRef}
          data-testid="product-search-input"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          placeholder="What are you looking for?"
          className="pl-10 bg-white text-foreground border-0 h-10"
          autoComplete="off"
        />
      </form>
      {open && term.trim().length >= 2 && (
        <div data-testid="search-suggestions"
          className="absolute left-0 right-0 top-12 rounded-xl border bg-white text-foreground shadow-xl overflow-hidden max-h-[70vh] overflow-y-auto z-50">
          {loading && results.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground">Searching…</div>
          ) : results.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground">No matches for “{term}”.</div>
          ) : (
            <>
              {results.map((p) => (
                <button key={p.id} data-testid={`suggestion-${p.sku}`} onClick={() => pick(p)}
                  className="w-full flex items-center gap-3 p-2.5 text-left hover:bg-slate-50 border-b last:border-b-0">
                  <span className="w-11 h-11 shrink-0 rounded-md border bg-slate-100 grid place-items-center overflow-hidden">
                    {p.image_url
                      ? <img src={p.image_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      : <Package size={18} className="text-slate-300" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium leading-snug line-clamp-1">{p.name}</span>
                    <span className="block text-xs text-muted-foreground">
                      {p.brand ? <span className="font-semibold text-steel">{p.brand}</span> : null}
                      {p.brand ? " · " : ""}<span className="font-mono">{p.sku}</span>
                    </span>
                  </span>
                  <span className="shrink-0 text-sm font-bold text-accent whitespace-nowrap">
                    {p.poa ? "POA" : money(p.price)}
                  </span>
                </button>
              ))}
              <button data-testid="search-see-all" onClick={goSearch}
                className="w-full p-3 text-center text-sm font-semibold text-steel bg-slate-50 hover:bg-slate-100">
                See all results for “{term.trim()}” →
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const { count, setOpen } = useCart();
  const { customer } = useCustomerAuth();
  const navigate = useNavigate();
  const [mobileSearch, setMobileSearch] = useState(false);
  const rd = useResponsive();
  const hf = useHeaderFooter();

  return (
    <header data-testid="site-header"
      className="sticky top-0 z-50 bg-steel/95 backdrop-blur-md border-b border-steel-light text-white">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2 sm:gap-4 h-14 sm:h-16">
          <MegaMenuMobile />
          <button data-testid="logo-home-btn" onClick={() => navigate("/")} aria-label="ACP Online home" className="flex items-center shrink min-w-0">
            <span className="grid place-items-center rounded-md bg-white px-2 py-1 sm:px-3 sm:py-1.5">
              <img src={acpLogo} alt="ACP Online" width="128" height="32" className={rd.header_mobile_logo === "full" ? "h-7 sm:h-8 w-auto max-w-[130px] sm:max-w-none" : "h-6 sm:h-8 w-auto max-w-[110px] sm:max-w-none"} />
            </span>
          </button>

          <div className="relative flex-1 max-w-xl hidden sm:block">
            <SearchField />
          </div>

          <div className="ml-auto flex items-center gap-1.5 sm:gap-3 shrink-0">
            {hf?.header?.links?.map((l) => (/^https?:/.test(l.link)
              ? <a key={l.id} href={l.link} target="_blank" rel="noreferrer" className="hidden lg:block text-sm text-slate-200 hover:text-accent whitespace-nowrap" data-testid={`header-link-${l.id}`}>{l.label}</a>
              : <Link key={l.id} to={l.link} className="hidden lg:block text-sm text-slate-200 hover:text-accent whitespace-nowrap" data-testid={`header-link-${l.id}`}>{l.label}</Link>))}
            {hf?.header?.phone && <a href={`tel:${hf.header.phone.replace(/\s/g, "")}`} className="hidden xl:flex items-center gap-1.5 text-sm text-slate-200 hover:text-accent whitespace-nowrap" data-testid="header-phone"><Phone size={14} className="text-accent" />{hf.header.phone}</a>}
            {(hf ? hf.header.gst_text : "Prices incl. GST") && <span className="hidden md:flex items-center gap-1.5 text-xs text-slate-300" data-testid="header-gst-text">
              <ShieldCheck size={14} className="text-accent" /> {hf ? hf.header.gst_text : "Prices incl. GST"}
            </span>}
            <button data-testid="mobile-search-toggle" onClick={() => setMobileSearch((v) => !v)} aria-label={mobileSearch ? "Close search" : "Search products"} aria-expanded={mobileSearch}
              className="sm:hidden grid place-items-center w-10 h-10 rounded-md bg-steel-light hover:bg-accent hover:text-steel transition-colors">
              {mobileSearch ? <X size={20} /> : <Search size={20} />}
            </button>
            {customer && typeof customer === "object" ? (
              <button data-testid="account-link" onClick={() => navigate("/account")} aria-label="My account"
                className="flex items-center gap-2 h-10 px-2.5 sm:px-3 rounded-md bg-steel-light hover:bg-accent hover:text-steel transition-colors text-sm font-medium max-w-[160px]">
                <User size={18} /><span className="hidden sm:block truncate">{customer.name || "Account"}</span>
              </button>
            ) : (
              <button data-testid="signin-link" onClick={() => navigate("/login")} aria-label="Sign in"
                className="flex items-center gap-2 h-10 px-2.5 sm:px-3 rounded-md bg-steel-light hover:bg-accent hover:text-steel transition-colors text-sm font-medium">
                <User size={18} /><span className="hidden sm:block">Sign in</span>
              </button>
            )}
            <button data-testid="open-cart-btn" onClick={() => setOpen(true)} aria-label={`Open cart, ${count} item${count === 1 ? "" : "s"}`}
              className="relative grid place-items-center w-10 h-10 rounded-md bg-steel-light hover:bg-accent hover:text-steel transition-colors">
              <ShoppingCart size={20} />
              {count > 0 && (
                <span data-testid="cart-count-badge"
                  className="absolute -top-1.5 -right-1.5 min-w-5 h-5 px-1 grid place-items-center rounded-full bg-accent text-steel text-xs font-bold">
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Mobile search row */}
        {mobileSearch && (
          <div className="sm:hidden pb-3" data-testid="mobile-search-row">
            <SearchField autoFocus onDone={() => setMobileSearch(false)} />
          </div>
        )}
      </div>
      <MegaMenuBar />
      <CartDrawer />
    </header>
  );
}
